import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { Checkbox } from "../components/ui/checkbox";
import useStore from "../helpers/useStore";
import { AppointmentContext } from "../stores/Appointment/appointment.provider";
import DialogDefaultAction from "../atoms/DialogDefaultAction";
import React, { useEffect, useState } from "react";
import { DateTimeRange, DateTimeRangePicker } from "../atoms/DateTimeRange";
import {
  AppointmentDto,
  TimeSlotDto,
  UpdateCancelledAppointmentDto,
  UpdateCancelledAppointmentDtoTimelinessEnum,
} from "../api-client";
import Controls from "../atoms/Controls";
import { TransactionsContext } from "../stores/Transactions/Transactions.provider";
import {
  recnungTransactionMethods,
  transactionMethods,
  transactionTypes,
} from "../components/Transactions/Kasse/v2/types/kasse.types";
import { UserContext } from "../stores/User/User.provider";
import { useUserAccounts } from "../stores/User/User.hooks";
import notificationStore from "../stores/Notification/notificationStore";
import moment from "moment";
import CancellationHistoryTable from "../components/PatientOverview/PatientHistory/CancellationHistoryTable";
import { TherapySlotRow } from "./TherapySlotRow";
import { cardShell } from "./cardShell";
import BatchCancelAppointmentDialog from "./BatchCancelAppointmentDialog";
import {
  useCancelAppointment,
  useCancelTimeSlot,
  useSaveBlockedSlotForPatient,
} from "../stores/Appointment/appointment.mutation";
import { useNoShowInvoiceTransaction } from "../stores/Transactions/transaction.mutations";
import { TerminViewConstants } from "../components/PatientOverview/TerminView";
import {
  useBlockedSlotsByPatient,
  useFilteredAppointmentsByPatient,
} from "../stores/Appointment/appointment.queries";
import {
  CancellationSide,
  INSURANCE_JUSTIFICATION_REASONS,
  NO_INSURANCE_REASON,
  VERMERK_SUGGESTIONS,
  toGapJustificationReason,
  vermerkTextFor,
} from "../helpers/gapJustificationReasons";

// "Keine versicherungsgültige Begründung" is a real, selectable answer rather
// than the empty state: the field is mandatory, so an unjustifiable gap has to
// be recorded deliberately instead of by leaving the dropdown alone.
const insuranceReasonOptions = [
  ...INSURANCE_JUSTIFICATION_REASONS.map((reason) => ({
    value: reason.value,
    label: reason.value,
  })),
  { value: NO_INSURANCE_REASON, label: NO_INSURANCE_REASON },
];

/**
 * The insurance-reason warning, on the dialog's own type scale. Stock MUI sizes
 * an Alert for a page, and at 14px/20px this one ran to three lines in a
 * one-third-width column — enough on its own to push the dialog into scrolling.
 */
const warningAlertSx = {
  mt: 0.5,
  mb: 0.5,
  py: 0.25,
  fontSize: "var(--text-dense)",
  lineHeight: 1.3,
  "& .MuiAlert-icon": { py: 0.5, mr: 1, fontSize: "var(--row-icon)" },
  "& .MuiAlert-message": { py: 0.5 },
};

// Equal-width toggle buttons that may shrink below their label width — the long
// "Stornieren ohne Nachverfolgung" label wraps inside its own button instead of
// pushing the group out of its column.
const toggleButtonSx = {
  fontSize: "12px",
  lineHeight: 1.2,
  px: 1,
  minWidth: 0,
  whiteSpace: "normal",
};

interface Props {
  patientId: number;
  appointment: AppointmentDto;
  isOpen: boolean;
  onClose: (actionPerformed: boolean) => void;
  isView?: TerminViewConstants;
  /** When opened from a specific calendar slot (context menu), pre-select only that slot. */
  initialSelectedTimeslotIds?: string[];
}

export const CancelAppointmentDialog: React.FC<Props> = ({
  patientId,
  appointment: appointmentData,
  isOpen,
  onClose,
  isView,
  initialSelectedTimeslotIds,
}) => {
  const AppointmentStore = useStore(AppointmentContext);
  const UserStore = useStore(UserContext);
  const { user } = UserStore;
  const accounts = useUserAccounts();
  // const [reason, setReason] = React.useState<string | undefined>(undefined);
  const [cancelAppDto, setCancelAppDto] = useState<UpdateCancelledAppointmentDto>({
    callStatus: "", // provide default values if necessary
    reason: "",
    noShowInvoice: false,
  });
  const [patientReason, setPatientReason] = React.useState<string | undefined>(undefined);
  const [therapistReason, setTherapistReason] = React.useState<string | undefined>(undefined);
  // Coded insurance-valid reason (e.g. "(K) Krankheit"). Drives whether a gap
  // justification gets created on the backend.
  const [insuranceReason, setInsuranceReason] = React.useState<string | undefined>(undefined);
  const [selectedTimeslotIds, setSelectedTimeslotIds] = useState<string[]>([]);
  const [accountSelection, setAccountSelection] = useState<string | null>(null);
  const [openBatchCancelDialog, setOpenBatchCancelDialog] = useState<boolean>(false);
  // Trigger fetching only when dialog opens
  const [shouldFetchFiltered, setShouldFetchFiltered] = useState(false);
  const { data: filteredAppointment, isLoading: isFilteredLoading } =
    useFilteredAppointmentsByPatient(
      patientId,
      {
        showOnlyFutureAppointments: false,
        showOnlyActiveRxs: false,
        showLastThreeMonths: false,
        rxIds: [],
        appointmentId: appointmentData?.id,
      },
      false,
      shouldFetchFiltered, // only enabled when dialog opens
    );

  useEffect(() => {
    // Conditional logic inside the effect is fine
    if (isOpen) {
      setShouldFetchFiltered(
        isView === TerminViewConstants.RX_VIEW || isView === TerminViewConstants.SERIES_VIEW,
      );
    }
  }, [isOpen, isView]);

  // Use filtered appointment if available, otherwise fallback to appointmentData
  const appointment = shouldFetchFiltered
    ? filteredAppointment && filteredAppointment.length > 0
      ? filteredAppointment[0]
      : appointmentData
    : appointmentData;

  const handleTimeslotToggle = (id: string) => {
    setSelectedTimeslotIds((prev) =>
      prev.includes(id) ? prev.filter((slotId) => slotId !== id) : [...prev, id],
    );
  };

  useEffect(() => {
    if (!isOpen || !appointment.timeSlots || appointment.timeSlots.length === 0) {
      return;
    }

    if (initialSelectedTimeslotIds?.length) {
      const validIds = initialSelectedTimeslotIds.filter((id) =>
        appointment.timeSlots!.some((slot) => slot.id === id),
      );
      if (validIds.length > 0) {
        setSelectedTimeslotIds(validIds);
        return;
      }
    }

    setSelectedTimeslotIds(appointment.timeSlots.map((slot) => slot.id!));
  }, [isOpen, appointment.timeSlots, initialSelectedTimeslotIds]);

  useEffect(() => {
    if (selectedTimeslotIds.length === 0) {
      setAccountSelection("");
      return;
    }

    const selectedSlots = appointment.timeSlots?.filter((slot: any) =>
      selectedTimeslotIds.includes(slot.id),
    );

    if (!selectedSlots || selectedSlots.length === 0) {
      setAccountSelection(null);
      return;
    }

    const accountIds = selectedSlots.map((slot: any) => slot.therapyRx?.rx?.account);

    const firstAccountId = accountIds[0];
    const allSame = accountIds.every((id: string) => id === firstAccountId);

    if (allSame && firstAccountId) {
      setAccountSelection(firstAccountId.id);
    } else {
      setAccountSelection(null);
    }
  }, [selectedTimeslotIds]);

  const [savingChanges, setSavingChanges] = React.useState(false);
  const [unavailabiltyRange, setUnavailabiltyRange] = React.useState<DateTimeRange>({
    startDate: new Date(appointment.start as string),
    endDate: new Date(appointment.end as string),
  });
  const { mutateAsync: cancelAppointment } = useCancelAppointment();
  const { mutateAsync: cancelTimeSlot } = useCancelTimeSlot();
  const { mutateAsync: saveBlockedSlotForPatient } = useSaveBlockedSlotForPatient();
  const { data: existingBlockedSlots } = useBlockedSlotsByPatient(patientId);
  const { getValueofAppointment } = AppointmentStore;
  const { mutateAsync: noShowInvoiceTransaction } = useNoShowInvoiceTransaction();
  const [whoCancelled, setWhoCancelled] = React.useState<"patient" | "therapist" | "no_trace">(
    "patient",
  );

  const handleChangeWhoCancelled = (
    event: React.MouseEvent<HTMLElement>,
    value: "patient" | "therapist",
  ) => {
    setWhoCancelled(value);
  };

  // Which side's Vermerk wording applies. The Begründung dropdown only renders
  // for patient/therapist, so "no_trace" never reaches the prefill.
  const cancellationSide: CancellationSide = whoCancelled === "therapist" ? "therapist" : "patient";

  // Selecting an insurance-valid reason pre-fills the editable Vermerk/Begründung
  // with that reason's wording for the cancelling side; the user may still type
  // their own text. "Keine versicherungsgültige Begründung" prefills nothing —
  // there is no coded reason to restate, so the user has to say why themselves.
  const handleInsuranceReasonChange = (value?: string) => {
    const next = value || undefined;
    setInsuranceReason(next);
    const text = vermerkTextFor(next, cancellationSide);
    if (!text) return;
    if (cancellationSide === "therapist") {
      setTherapistReason(text);
    } else {
      setPatientReason(text);
    }
  };

  // The Vermerk text to store as the cancellation reason. If the user left the
  // Vermerk blank but picked an insurance-valid Begründung, fall back to that
  // reason's wording instead of blocking them.
  const getEffectiveCancellationReason = () =>
    cancelAppDto.reason || vermerkTextFor(insuranceReason, cancellationSide) || "";

  const handleAppointmentCancellation = async () => {
    let shouldSkipFinally = false;

    try {
      // Use the Vermerk text, falling back to the insurance Begründung's
      // un-coded text when the Vermerk was left blank.
      const effectiveReason = getEffectiveCancellationReason();
      // Check if cancellation reason is provided
      if (whoCancelled === "patient" || whoCancelled === "therapist") {
        // The coded Begründung is mandatory. "Keine versicherungsgültige
        // Begründung" is a valid answer — leaving the field untouched is not,
        // so an unjustified gap is always a decision someone made.
        if (!insuranceReason) {
          notificationStore.showMessage(
            "Bitte wählen Sie eine versicherungsgültige Begründung aus.",
            "warning",
          );
          shouldSkipFinally = true;
          return;
        }
        if (!effectiveReason) {
          notificationStore.showMessage(
            "Der Grund für die Stornierung ist zwingend erforderlich!!!",
            "warning",
          );
          shouldSkipFinally = true; // Set the flag to skip finalization
          return;
        }
      }

      const cancelAppDtoToSend = { ...cancelAppDto, reason: effectiveReason };

      const totalSlots = appointment.timeSlots?.length || 0;
      const selectedTimeSlots =
        appointment.timeSlots?.filter((slot) => selectedTimeslotIds.includes(slot.id!)) || [];
      const selectedSlots = selectedTimeSlots.length;

      if (!selectedSlots) {
        notificationStore.showMessage(
          "Bitte wählen Sie mindestens einen Zeitslot zur Stornierung aus.",
          "warning",
        );
        return;
      }
      let cancelledResults: any[] = [];

      const hasGroupParticipantSelected = selectedTimeSlots.some(
        (slot) =>
          slot.type === "GroupTimeSlotPatient" || slot.type === "ContinuousGroupTimeSlotPatient",
      );

      if (selectedSlots === totalSlots && !hasGroupParticipantSelected) {
        // Cancel full appointment — safe only when no KGG participant is involved.
        const cancelledAppt = await cancelAppointment({
          appointmentId: appointment.id as string,
          byTherapist: whoCancelled === "therapist",
          cancelAppDto: cancelAppDtoToSend,
          patientId: patientId,
        });
        cancelledResults = [cancelledAppt];
      } else {
        // Per-slot cancel (required for KGG participants so siblings can survive).
        for (const slot of selectedTimeSlots) {
          const result = await cancelTimeSlot({
            slotId: slot.id!,
            byTherapist: whoCancelled === "therapist",
            cancelAppDto: cancelAppDtoToSend,
            patientId: patientId,
          });
          cancelledResults.push(result);
        }
      }

      // Record the patient as unavailable for the cancelled window ONCE (not once
      // per cancelled slot). Re-saving the same window — or a window the patient
      // is already marked unavailable for — would create duplicate blocks and
      // trip the backend's global duplicate-start guard. So skip when an
      // overlapping block already exists, and never let a failed block-save throw
      // an uncaught rejection that crashes the (already-completed) cancellation.
      if (whoCancelled === "patient") {
        const blockStartMs = new Date(unavailabiltyRange.startDate).getTime();
        const blockEndMs = new Date(unavailabiltyRange.endDate).getTime();
        const alreadyBlocked = (existingBlockedSlots ?? []).some((slot) => {
          const slotStartMs = slot.start ? new Date(slot.start).getTime() : NaN;
          const slotEndMs = slot.end ? new Date(slot.end).getTime() : NaN;
          return slotStartMs < blockEndMs && blockStartMs < slotEndMs;
        });

        if (!alreadyBlocked) {
          const timeSlot: TimeSlotDto = {
            start: new Date(unavailabiltyRange.startDate).toISOString(),
            end: new Date(unavailabiltyRange.endDate).toISOString(),
            type: "PatientUnvailable",
          };
          try {
            await saveBlockedSlotForPatient({
              patientId: patientId,
              blockedSlots: [timeSlot],
            });
          } catch (blockError) {
            notificationStore.showMessage(
              "Die Stornierung wurde gespeichert, aber die Patienten-Unverfügbarkeit konnte nicht eingetragen werden.",
              "warning",
            );
          }
        }
      }

      for (const cancelledAppt of cancelledResults) {
        if (cancelledAppt?.noShowInvoice) {
          const response = await getValueofAppointment(cancelledAppt.id);

          // const tarifGroupIds = cancelledAppt.therapyRxs.map(
          //   (therapyRx: any) => therapyRx.rx?.tarifGroup?.id,
          // );

          // // const allSameAccount = accountIds.every((id: any) => id === accountIds[0]);
          // const allSameTarifGroup = tarifGroupIds.every(
          //   (id: any) => id === tarifGroupIds[0],
          // );
          const tarifGroupIds = (
            cancelledAppt?.appointment?.timeSlots ??
            appointment.timeSlots ??
            []
          )
            .map((slot: TimeSlotDto) => slot.therapyRx?.rx?.tarifGroupId)
            .filter((id: string | undefined): id is string => Boolean(id));

          // const allSameAccount = accountIds.every((id: any) => id === accountIds[0]);
          const allSameTarifGroup = tarifGroupIds.every((id: any) => id === tarifGroupIds[0]);

          if (!allSameTarifGroup) {
            throw new Error("Die Tarifgruppen-IDs sind unterschiedlich.");
          }

          // let accountName = accountIds[0] || "";
          if (response) {
            const transactionData = {
              text: `Ausgefallener Termin am ${moment(cancelledAppt.createdAt).format("DD.MM.YYYY")}`,
              type: transactionTypes.EINNAHME,
              amount: Number(response.totalDueAmount.toFixed(2)),
              method: recnungTransactionMethods.RECHNUNG,
              accountId: accountSelection!,
              userId: user?.id || "",
              wasExported: false,
              patientId: patientId,
              textDescription: "Ausfallrechnung",
              tAccountId: tarifGroupIds[0] === "Privat" ? 4105 : 4101,
            };

            const data = await noShowInvoiceTransaction({
              appointmentId: cancelledAppt.appointment.id,
              rechnungDto: transactionData,
              patientId: patientId,
            });
            if (data && data.docDto && data.docDto.accessUrl) {
              // load the pDF from the url in a new window
              // console.log("Opening PDF in new window:", data.docDto.accessUrl);
              window.open(data.docDto.accessUrl, "_blank");
            } else {
              console.error("No access URL found in transaction data:", data);
            }
          }
        }
      }

      onClose(true);
    } catch (error: any) {
      console.error("Fehler:", error);
      notificationStore.showMessage(error?.message, "error");
    } finally {
      setSavingChanges(false);
      if (shouldSkipFinally) return;
      onClose(true);
    }
  };

  // Automatically set timeliness based on current time and appointment start time
  useEffect(() => {
    if (whoCancelled === "patient") {
      // Only execute for patient cancellations

      const currentTime = new Date().getTime();
      const appointmentStartTime = new Date(appointment.start as string).getTime();
      const timeDifferenceInHours = (appointmentStartTime - currentTime) / (1000 * 60 * 60);

      if (timeDifferenceInHours > 24) {
        setCancelAppDto((prev) => ({
          ...prev,
          timeliness: UpdateCancelledAppointmentDtoTimelinessEnum.Fristgerecht24StVorher,
        }));
      } else if (timeDifferenceInHours <= 24 && timeDifferenceInHours > 0) {
        setCancelAppDto((prev) => ({
          ...prev,
          timeliness: UpdateCancelledAppointmentDtoTimelinessEnum.Nichtfristgerecht,
        }));
      } else {
        setCancelAppDto((prev) => ({
          ...prev,
          timeliness: UpdateCancelledAppointmentDtoTimelinessEnum.NichtErschienen,
        }));
      }
    }
  }, [appointment.start, whoCancelled]); // Also depend on `whoCancelled`

  useEffect(() => {
    if (whoCancelled === "therapist") {
      setCancelAppDto((prevState) => ({
        ...prevState,
        reason: therapistReason || "",
        noShowInvoice: false,
        timeliness: undefined,
      }));
    } else if (whoCancelled === "patient") {
      setCancelAppDto((prevState) => ({
        ...prevState,
        reason: patientReason || "",
      }));
    } else if (whoCancelled === "no_trace") {
      setCancelAppDto((prevState) => ({
        ...prevState,
        reason: "", // No reason for no_trace
        noShowInvoice: false,
        timeliness: undefined,
      }));
    }
  }, [whoCancelled, therapistReason, patientReason]);

  // The Vermerk wording is side-specific, so switching sides after picking a
  // Begründung would otherwise leave the new side's Vermerk empty and block the
  // cancel with a mandatory-reason warning. Seed it — but never overwrite text
  // that is already there, so the user's own wording survives a side switch.
  useEffect(() => {
    const text = vermerkTextFor(insuranceReason, cancellationSide);
    if (!text) return;
    if (cancellationSide === "therapist") {
      setTherapistReason((prev) => prev || text);
    } else {
      setPatientReason((prev) => prev || text);
    }
  }, [cancellationSide, insuranceReason]);

  // Thread the coded insurance reason into the cancel DTO. When cancelling
  // without tracing (no_trace) — or when the user recorded that there is no
  // insurance-valid reason — no gap justification is ever created.
  useEffect(() => {
    setCancelAppDto((prevState) => ({
      ...prevState,
      gapJustificationReason:
        whoCancelled === "no_trace" ? undefined : toGapJustificationReason(insuranceReason),
    }));
  }, [insuranceReason, whoCancelled]);

  const handleOk = () => {
    setSavingChanges(true);
    if (!accountSelection && cancelAppDto.noShowInvoice) {
      notificationStore.showMessage("Bitte wählen Sie einen Mandanten aus.", "warning");
      setSavingChanges(false);
      return;
    }
    handleAppointmentCancellation();
  };

  const handleCloseBatchCancel = () => {
    setOpenBatchCancelDialog(!openBatchCancelDialog);
    onClose(true);
  };

  // Both lists name their side, so a Vermerk read on its own — in the
  // cancellation history or on an insurance query — says who caused the gap.
  const optionsForPatient = VERMERK_SUGGESTIONS.patient;
  const optionsForTherapist = VERMERK_SUGGESTIONS.therapist;
  // Show loading only if view requires filtered appointment
  if (isFilteredLoading) {
    return <CircularProgress />;
  }

  return (
    <Dialog
      open={isOpen}
      onClose={() => onClose(false)}
      fullWidth
      // `xl`, not `lg`: the three columns have to hold a slot list, a form and
      // a seven-column history table at once. At 1200px the history lost its
      // last two columns to its own scrollbar and the form wrapped every label,
      // which is what pushed the dialog itself into scrolling.
      maxWidth="xl"
      // Fixed geometry: the three "eingeleitet durch" options render very
      // different amounts of content, and without this the paper resizes on
      // every toggle. Content scrolls inside DialogContent instead.
      sx={{ "& .MuiDialog-paper": { height: "min(760px, 92vh)" } }}
    >
      <DialogTitle>Termin stornieren</DialogTitle>
      <DialogContent>
        <Grid container spacing={2}>
          {/* Column 1: Time Slots Selection */}
          <Grid item xs={2}>
            <div className="mb-1 text-title font-semibold text-ink-text">
              Zu stornierende Behandlungen
            </div>
            {/*
              A narrow column inside a wide window is exactly what the density
              token is for (design-system: density is a property of the
              container). The slots themselves are the same card + row as the
              Termin card and the calendar, so a treatment looks the same
              wherever you meet it.
            */}
            <div
              data-density="compact"
              className="flex flex-col gap-1 rounded-container border border-solid border-ink-line bg-surface-sunken p-1"
            >
              {appointment.timeSlots?.map((timeSlot: any) => (
                <div key={timeSlot.id} className={`${cardShell()} px-1 py-0.5`}>
                  <TherapySlotRow
                    timeSlot={timeSlot}
                    appointment={appointment}
                    displayTimeslotTime
                    trailing={
                      <Checkbox
                        checked={selectedTimeslotIds.includes(timeSlot.id)}
                        onCheckedChange={() => handleTimeslotToggle(timeSlot.id)}
                        className="h-3.5 w-3.5"
                        aria-label="Behandlung stornieren"
                      />
                    }
                  />
                </div>
              ))}

              {appointment.reason && !appointment.end && (
                <div className="px-1 text-mini text-ink-muted">* {appointment.reason}</div>
              )}
            </div>
          </Grid>

          {/* Column 2: Cancellation Actions */}
          <Grid item xs={4}>
            <div className="mb-1 text-title font-semibold text-ink-text">
              Stornierung eingeleitet durch ...
            </div>
            <ToggleButtonGroup
              value={whoCancelled}
              onChange={handleChangeWhoCancelled}
              exclusive
              fullWidth
              size="small"
              sx={{ mb: 2 }}
            >
              <ToggleButton value="patient" sx={toggleButtonSx}>
                Patient
              </ToggleButton>
              <ToggleButton value="therapist" sx={toggleButtonSx}>
                Therapeut
              </ToggleButton>
              <ToggleButton value="no_trace" sx={toggleButtonSx}>
                Stornieren ohne Nachverfolgung
              </ToggleButton>
            </ToggleButtonGroup>

            {whoCancelled === "patient" && (
              <>
                <Controls.Select
                  name="timeliness"
                  label="Aktualität"
                  onChange={(e: any) =>
                    setCancelAppDto((prev) => ({
                      ...prev,
                      timeliness: e.target.value as UpdateCancelledAppointmentDtoTimelinessEnum,
                    }))
                  }
                  value={cancelAppDto.timeliness}
                  options={Object.values(UpdateCancelledAppointmentDtoTimelinessEnum).map(
                    (value) => ({
                      value,
                      label: value,
                    }),
                  )}
                />
                <Controls.Checkbox
                  name="noShowInvoice"
                  label="Ausfallrechnung"
                  value={cancelAppDto.noShowInvoice}
                  onChange={(e: any) =>
                    setCancelAppDto((prev) => ({
                      ...prev,
                      noShowInvoice: e.target.value,
                    }))
                  }
                />
                {cancelAppDto.noShowInvoice && (
                  <Grid item xs={6}>
                    <Controls.Select
                      name="accountId"
                      label="Mandant"
                      id={1}
                      value={accountSelection}
                      onChange={(e: any) => setAccountSelection(e.target.id)}
                      options={accounts?.map((option: any) => ({
                        label: option?.name?.toString(),
                        value: option?.name?.toString(),
                        id: option?.id?.toString(),
                      }))}
                      style={{
                        width: "100%",
                        marginTop: "0px",
                        marginBottom: "0px",
                        fontSize: "10px",
                      }}
                      required
                    />
                  </Grid>
                )}
                <Controls.Select
                  name="insuranceReason"
                  label="Versicherungsgültige Begründung"
                  value={insuranceReason || ""}
                  onChange={(e: any) => handleInsuranceReasonChange(e.target.value)}
                  options={insuranceReasonOptions}
                  showOptionLabel
                  required
                />
                {/* Warns both before a choice is made and after the user
                    deliberately recorded that no valid reason exists — the gap
                    goes unjustified either way. */}
                {(!insuranceReason || insuranceReason === NO_INSURANCE_REASON) && (
                  <Alert severity="warning" sx={warningAlertSx}>
                    Ohne versicherungsgültige Begründung wird diese Stornierung von der Krankenkasse
                    nicht als begründete Lücke anerkannt.
                  </Alert>
                )}
                <Autocomplete
                  freeSolo
                  options={optionsForPatient}
                  value={patientReason ?? ""}
                  onInputChange={(e, newValue) => setPatientReason(newValue || undefined)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Vermerk"
                      variant="outlined"
                      fullWidth
                      margin="dense"
                    />
                  )}
                />
                <Typography sx={{ mt: 1 }}>
                  Der Patient ist für die folgende Zeitspanne nicht verfügbar
                </Typography>
                <DateTimeRangePicker range={unavailabiltyRange} onChange={setUnavailabiltyRange} />
              </>
            )}

            {whoCancelled === "therapist" && (
              <>
                <Controls.Select
                  name="insuranceReason"
                  label="Versicherungsgültige Begründung"
                  value={insuranceReason || ""}
                  onChange={(e: any) => handleInsuranceReasonChange(e.target.value)}
                  options={insuranceReasonOptions}
                  showOptionLabel
                  required
                />
                {/* Warns both before a choice is made and after the user
                    deliberately recorded that no valid reason exists — the gap
                    goes unjustified either way. */}
                {(!insuranceReason || insuranceReason === NO_INSURANCE_REASON) && (
                  <Alert severity="warning" sx={warningAlertSx}>
                    Ohne versicherungsgültige Begründung wird diese Stornierung von der Krankenkasse
                    nicht als begründete Lücke anerkannt.
                  </Alert>
                )}
                <Autocomplete
                  freeSolo
                  options={optionsForTherapist}
                  value={therapistReason ?? ""}
                  onInputChange={(e, newValue) => setTherapistReason(newValue || undefined)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Begründung"
                      variant="outlined"
                      fullWidth
                      margin="dense"
                    />
                  )}
                />
              </>
            )}

            {/* The option's own button already says what it is; this says what
                it does, which is the part that is not obvious. */}
            {whoCancelled === "no_trace" && (
              <div className="text-dense text-ink-muted">
                Der Termin wird ohne Grund und ohne Eintrag im Stornierungsverlauf entfernt.
              </div>
            )}
          </Grid>

          {/* Column 3: Previous Cancellations — the widest column: seven
              columns of history, and the only one that grows with the data. */}
          <Grid item xs={6}>
            <div className="mb-1 text-title font-semibold text-ink-text">
              Vorherige Stornierungen
            </div>
            <Box
              className="rounded-card border border-solid border-ink-edge bg-card"
              sx={{ overflow: "hidden" }}
            >
              <CancellationHistoryTable patientId={patientId} enabled={isOpen} maxHeight={550} />
            </Box>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        {savingChanges ? (
          <CircularProgress size={24} />
        ) : (
          <>
            <DialogDefaultAction
              onAction={handleOk}
              disabled={!whoCancelled && !accountSelection}
            />
            <Button onClick={() => onClose(false)} variant="text">
              Abbrechen
            </Button>
            <Button disabled={!whoCancelled && !accountSelection} onClick={handleOk}>
              Bestätigen
            </Button>
            <Button
              onClick={() => {
                if (
                  (whoCancelled === "patient" || whoCancelled === "therapist") &&
                  !getEffectiveCancellationReason()
                ) {
                  notificationStore.showMessage(
                    "Der Grund für die Stornierung ist zwingend erforderlich!!!",
                    "warning",
                  );
                  return;
                }
                if (whoCancelled === "patient" && cancelAppDto.noShowInvoice && !accountSelection) {
                  notificationStore.showMessage("Bitte wählen Sie ein Mandant aus.", "warning");
                  return;
                }
                setOpenBatchCancelDialog(true);
              }}
              color="primary"
              variant="contained"
              disabled={!whoCancelled}
            >
              Mehrere Termine absagen/löschen
            </Button>
          </>
        )}
      </DialogActions>
      {openBatchCancelDialog && (
        <BatchCancelAppointmentDialog
          open={openBatchCancelDialog}
          patientId={patientId}
          cancelAppDto={{
            ...cancelAppDto,
            reason: getEffectiveCancellationReason(),
          }}
          whoCancelled={whoCancelled}
          unavailabiltyRange={unavailabiltyRange}
          onClose={handleCloseBatchCancel}
          accountSelected={accountSelection}
          appointment={appointment}
        />
      )}
    </Dialog>
  );
};
